import { actionChannel, call, delay, put, select, take, takeEvery, takeLeading } from 'redux-saga/effects'
import { get, map, forEach, set, isEmpty, reduce, times, cloneDeep, isString, isPlainObject, find, each } from 'lodash'
import { toast } from 'react-toastify'

import { nextPosOrderNumber } from '../utils/orderNumbers'
import { calculateTax, calculateSubtotal } from '../utils/totalUtils'
import { getNetworkAvailableValue } from '../utils/networkConnected'
import { getIsKiosk } from '../selectors/menus'
import { getSafIsUploading } from '../selectors/peripheral'

import { APPLICATION_STRINGS } from '../strings'

import moment from 'moment'

import {
  createOfflineOrderSucceeded,
  createOfflineOrderFailed,
  REPLAY_AUTH_REQUESTED,
  replayAuthRequestSucceeded,
  replayAuthRequestFailed,
  saveOrder,
  saveOfflineOrder,
  START_OFFLINE_CARD_ORDER,
  START_ONLINE_CARD_ORDER,
  SYNC_ORDER_REQUESTED,
  syncOrderInitiated,
  syncOrderRequested,
  syncOrderSucceeded,
  syncOrderFailed,
  SYNC_OFFLINE_ORDER_REQUESTED,
  syncOfflineOrderRequested,
  updateOrderSucceeded,
  updateOrderFailed,
  addLineItemsSucceeded,
  addLineItemsFailed,
  UPDATE_CARD_READER_DATA,
  CLOSE_NO_OP_TENDER,
  SEND_ORDER_EMAIL,
  SYNC_ORDERS_REQUESTED,
  SYNC_REPLAY_AUTH_ORDERS_REQUESTED,
  replayAuthRequested,
  updateOrderInProgress,
  ADD_PAYMENT_TO_ORDER_IN_PROGRESS,
  SAVE_AND_SYNC_ORDER_IN_PROGRESS,
} from '../actions/order'

import { SAVE_RICH_CHECKOUT_ORDER_REQUESTED, saveRichCheckoutOrder } from '../actions/richCheckout/order'
import { clearRemoteOrderTotal } from '../actions/orderTotalRemote'
import { clearCart } from '../actions/cart'

import Remote from '../remote'

import CartItemViewModel from '../viewmodels/CartItemViewModel'
import { makeGetCartItems, getTaxRate, getTaxByItem } from '../selectors/cart'
import { makeGetOrder, getOfflineLocalIds, getOfflineCardIds, getOrderInProgress } from '../selectors/order'
import { getCurrentMenu } from '../selectors/menus'
import { getDeviceSubtype } from '../selectors/config'

import { setSafUploadComplete } from '../actions/peripheral'

import PeripheralBridge from '../utils/peripheralBridge'
import { approvedOfflineThenDeclined, responseStatusString, statusCheckComplete } from '../utils/offlineReplayStatuses'
import { fakeCreditCardResponse, showFakeCreditCard } from '../utils/fakeCreditCardResponse'
import { customHistory } from '../stores'
import { getActiveAttendantId, getAttendants } from '../selectors/attendant'
import { getRemoteOrderTotal, makeGetRemoteOrderTotal } from '../selectors/orderTotalRemote'
import { centsToFloat } from '../utils/formatters'
import { getSelectedPromotions } from '../selectors/promotion'
import { cardReaderApproved } from '../utils/orderUtils'
import DeviceSubtypes from '../utils/deviceSubtypes'
import createDataDogLog from '../utils/datadog'

// Gateway timeouts from platform show ~30s in DataDog
// FCC default timeout is 35s, PAX is 60s, split the difference to 45
const MS_GATEWAY_FORCE_CONTINUE = 45_000

const generateUuid = require('uuid/v4')
const getCartItems = makeGetCartItems()
const getOrder = makeGetOrder()

function* verifyCurrentAttendant(isKiosk) {
  const attendantsExist = !isEmpty(yield select(getAttendants))

  if (!attendantsExist || isKiosk) {
    return null
  }

  const employeeId = yield select(getActiveAttendantId)

  if (employeeId) {
    return employeeId
  }

  yield call(directTo, 'push', '/user-login')
  toast.error(APPLICATION_STRINGS.PLEASE_LOGIN)
  createDataDogLog('error', APPLICATION_STRINGS.PLEASE_LOGIN)
  return undefined
}

const expandLineItems = (lineItems) => {
  const clonedLinedItems = cloneDeep(lineItems)

  const result = reduce(clonedLinedItems, (accumulator, lineItem) => {
    const clones = times((lineItem.quantity || 1), () => {
      const clone = cloneDeep(lineItem)
      clone.quantity = 1

      return clone
    })

    return [ ...accumulator, ...clones ]
  }, [])

  return result
}

const makeLineItemData = (lineItems) => {
  const expandedLineItems = expandLineItems(lineItems)

  const itemModels = expandedLineItems.map((lineItem, index) => {
    // since the view model is memoizing, passing in index as the first attribute
    // to make sure they are all treated uniquely
    return CartItemViewModel({ ...index, ...lineItem })
  })

  forEach(itemModels, (item) => {
    set(item, 'lineItemUuid', generateUuid())
  })

  return itemModels
}

const parseModifiers = (modifiers) => {
  const mappedModifiers = map(modifiers, (modifier) => {
    return {
      menuItemUuid: modifier.id,
      quantity: 1,
    }
  })

  return [mappedModifiers]
}

const makeCreateOrderLineItems = (itemModels = []) => {
  return itemModels.map((item) => {
    const modifiers = parseModifiers(item.modifiers)

    return {
      uuid: item.lineItemUuid,
      menuItemUuid: item.id,
      quantity: 1,
      price: +centsToFloat(item.priceInCents),
      modifiers,
      notes: item.notes,
    }
  })
}

const makeCreateOrderParams = ({ amountInCents, tipAmountInCents, signature, itemModels, menuUuid,
    orderNumber, paymentType, submittedAt, uuid, isTab, orderState, employeeId, affiliations,
    promotions }) => {
  const shape = {
    clientCreatedAt: moment(submittedAt).toISOString(),
    standMenuUuid: menuUuid,
    orderMenuItems: makeCreateOrderLineItems(itemModels),
    orderNumber,
    payments: [
      {
        paymentType,
        amountInCents,
      }
    ],
    webOrder: true,
    uuid,
    affiliations,
    promotions,
    tipAmountInCents,
  }

  if (signature) {
    shape.receiptSignature = signature
  }

  // TODO(mkramerl): Utilize our actual platform order states across the entire POS rather than
  // 'open', 'closed', 'failed'.
  if (orderState === 'failed') {
    shape.state = 'submission_failed'
  }

  if (orderState === 'preauth') {
    shape.state = 'submitted'
  }

  if (employeeId) {
    shape.employeeId = employeeId
  }

  return shape
}

const getUserNameFromCard = (cardReaderData) => {
  // example: "OBERG/PETER               "
  // super defensive with the string methods below because I don't know much about different possibilities
  const name = cardReaderData.cardholderName || "/"
  const split = name.split("/")
  const firstName = (split[1] || "").trim()
  const lastName = (split[0] || "").trim()

  return `${firstName} ${lastName}`
}

// called user_attributes in stadium, but it mostly contains credit card attributes
const makeUserAttributes = (cardReaderData) => {
  const { authResponseData, receiptData } = cardReaderData

  const { authorizationCode, processorTransactionId, reconciliationId } = authResponseData || {}
  const { approvalCode, authStatus, maskedCardNumber, maskedPan } = receiptData || {}

  const safeFour = maskedCardNumber || ""
  const lastFour = safeFour.substring(safeFour.length - 4)

  const token = cardReaderData?.tokenInformation?.token
  const deviceId = localStorage.getItem('device_id')

  return {
    last_4: lastFour,

    // needed in this format for refunds
    freedompayRequestId: cardReaderData.requestId,
    freedompayMerchantReferenceCode: cardReaderData.merchantReferenceCode || cardReaderData.uuid,
    freedompayInvoiceNumber: cardReaderData.invoiceNumber,
    freedompayIssuerName: cardReaderData.cardIssuer || cardReaderData.cardType,
    freedompayStoreId: cardReaderData.storeId,
    freedompayTerminalId: cardReaderData.terminalId,

    // extra data to help troubleshoot customer charge back requests
    authorizationCode,
    processorTransactionId,
    reconciliationId,
    approvalCode,
    authStatus,
    maskedCardNumber,
    maskedPan,
    token,
    deviceId,
  }
}

export function* closeNoOpTenderSaga(params = {}) {
  const deviceSubtype = yield select(getDeviceSubtype)
  const isKiosk = DeviceSubtypes.isKiosk(deviceSubtype)
  const employeeId = yield verifyCurrentAttendant(isKiosk)
  if (employeeId === undefined) return

  const tender = params?.payload?.tender ?? { name: 'cash', displayName: 'Cash' }
  const uuid = !!params?.payload?.orderUuid ? params.payload.orderUuid : generateUuid()
  const tipAmountInCents = params?.payload?.tipAmountInCents ?? 0
  const orderNumber = nextPosOrderNumber()
  const submittedAt = new Date()

  const paymentCompleted = true

  const { menuUuid, revenueCenterUuid, revenueName } = yield menuAndRvcUuids(isKiosk)

  const lineItems = yield select(getCartItems)
  const itemModels = makeLineItemData(lineItems)

  const taxRate = yield select(getTaxRate)
  const taxByItem = yield select(getTaxByItem)

  const getRemoteOrderTotal = makeGetRemoteOrderTotal()
  const remoteOrderTotal = yield select(getRemoteOrderTotal)
  const taxAmountInCents = remoteOrderTotal?.isAvailable ? remoteOrderTotal?.taxAmountInCents ?? 0 : calculateTax(lineItems, taxRate, taxByItem)
  const amountInCents = (remoteOrderTotal?.totalAmountInCents ?? calculateSubtotal(lineItems) + taxAmountInCents) + tipAmountInCents
  const subtotalAmountInCents = remoteOrderTotal?.subtotalInCents ?? amountInCents - taxAmountInCents
  const discountAmountInCents = remoteOrderTotal?.discountAmountInCents ?? 0
  const affiliations = remoteOrderTotal?.isAvailable ? [] : null
  const serviceFee = remoteOrderTotal?.serviceChargeInCents ? { name: remoteOrderTotal?.serviceChargeName, total: remoteOrderTotal?.serviceChargeInCents } : null

  if (isEmpty(lineItems)) {
    yield call(directTo, 'push', '/concession-order')
    toast.error(APPLICATION_STRINGS.ORDER_ONE_ITEM)
    createDataDogLog('error', APPLICATION_STRINGS.ORDER_ONE_ITEM)
    return
  }

  let promotions = yield select(getSelectedPromotions)
  if (isEmpty(promotions) || !remoteOrderTotal.isAvailable) {
    promotions = undefined
  }

  const createOrderParams = makeCreateOrderParams({
    amountInCents,
    itemModels,
    menuUuid,
    orderNumber,
    paymentType: tender.name,
    submittedAt,
    orderState: 'closed',
    uuid,
    employeeId,
    affiliations,
    promotions,
    tipAmountInCents,
  })

  const orderData = {
    amountInCents,
    displayName: tender.displayName,
    subtotalAmountInCents,
    taxAmountInCents,
    discountAmountInCents,
    tipAmountInCents,
    serviceFee,
    createOrderParams,
    itemModels,
    paymentCompleted,
    orderNumber,
    submittedAt,
    orderState: 'closed',
    uuid,
    revenueCenterUuid,
    revenueName,
  }

  yield put(saveOrder(orderData))
  yield put(syncOrderRequested(uuid))
  yield put(clearRemoteOrderTotal())
  if (!isKiosk) {
    yield call(directTo, 'push', `/receipt/${uuid}`)
  }
}

function* menuAndRvcUuids(isKiosk) {
  let menuUuid = yield select((state) => get(state, 'config.configuration.menuUuids[0]', ''))
  let revenueCenterUuid = yield select((state) => get(state, 'revenueCenter.activeRevenueCenter', 'unassigned'))
  let revenueName = yield select((state) => get(state, `menus.byId.${menuUuid}.standName`, 'unassigned'))

  if (isKiosk) {
    menuUuid = yield select((state) => get(state, 'appState.currentMenuId'))
    revenueCenterUuid = yield select((state) => get(state, `menus.byId.${menuUuid}.standUuid`, 'unassigned'))
    revenueName = yield select((state) => get(state, `menus.byId.${menuUuid}.standName`, 'unassigned'))
  }

  return { menuUuid, revenueCenterUuid, revenueName }
}

export function* sendOrderEmail(params = {}) {
  const payload = params.payload

  try {
    yield call(Remote.sendEmailJobToStadium, payload)
    toast(APPLICATION_STRINGS.EMAIL_SENT)
  } catch (err) {
    toast(APPLICATION_STRINGS.EMAIL_FAILED + err)
  }
  return
}

export function* watchSendOrderEmail() {
  yield takeEvery(SEND_ORDER_EMAIL, sendOrderEmail)
}

export function* startOnlineCardOrder(params = {}) {
  const isKiosk = get(params, 'payload.isKiosk', false)
  const employeeId = yield verifyCurrentAttendant(isKiosk)
  if (employeeId === undefined) return

  const cardReaderData = get(params, 'payload.cardReaderData', {})
  const uuid = get(cardReaderData, 'uuid', generateUuid())
  const orderNumber = cardReaderData?.orderNumber ?? params?.payload?.orderNumber
  const signature = params?.payload?.signature
  const tipAmountInCents = params?.payload?.tipAmountInCents ?? 0

  const submittedAt = new Date()
  const paymentCompleted = cardReaderData.transType === 'sale'
  const isTab = false
  const orderState = get(params, 'payload.orderState', 'closed')

  const { menuUuid, revenueCenterUuid, revenueName } = yield menuAndRvcUuids(isKiosk)
  const menu = yield select((state) => getCurrentMenu(state))
  const menuSubtype = get(menu, 'subtype')

  // for kiosk orders, we're passing in line items to avoid race condition of cart being cleared
  // when redirecting to the attract loop
  let lineItems = get(params, 'payload.lineItems')
  if (!lineItems) lineItems = yield select(getCartItems)
  if (isEmpty(lineItems)) {
    yield call(directTo, 'push', '/concession-order')
    toast.error(APPLICATION_STRINGS.ORDER_ONE_ITEM)
    createDataDogLog('error', APPLICATION_STRINGS.ORDER_ONE_ITEM)
    return
  }
  const itemModels = makeLineItemData(lineItems)

  const taxRate = yield select(getTaxRate)
  const taxByItem = yield select(getTaxByItem)
  const getRemoteOrderTotal = makeGetRemoteOrderTotal()
  const remoteOrderTotal = yield select(getRemoteOrderTotal)
  const taxAmountInCents =  remoteOrderTotal?.isAvailable ? remoteOrderTotal?.taxAmountInCents ?? 0 : calculateTax(lineItems, taxRate, taxByItem)
  const amountInCents = (remoteOrderTotal?.totalAmountInCents ?? calculateSubtotal(lineItems) + taxAmountInCents) + tipAmountInCents
  const subtotalAmountInCents = remoteOrderTotal?.subtotalInCents ?? amountInCents
  const discountAmountInCents = remoteOrderTotal?.discountAmountInCents ?? 0
  const serviceFee = remoteOrderTotal?.serviceChargeInCents ? { name: remoteOrderTotal?.serviceChargeName, total: remoteOrderTotal?.serviceChargeInCents } : null
  const userAttributes = makeUserAttributes(cardReaderData)
  let promotions = yield select(getSelectedPromotions)
  if (isEmpty(promotions) || !remoteOrderTotal.isAvailable) {
    promotions = undefined
  }

  const createOrderParams = {
    ...makeCreateOrderParams({
      amountInCents,
      tipAmountInCents,
      signature,
      employeeId,
      itemModels,
      menuUuid,
      orderNumber,
      paymentType: 'freedompay_credit',
      submittedAt,
      isTab,
      orderState,
      uuid,
      affiliations: [],
      promotions,
    }),
    userAttributes: userAttributes,
    userName: getUserNameFromCard(cardReaderData),
  }

  const orderData = {
    amountInCents,
    tipAmountInCents,
    subtotalAmountInCents,
    discountAmountInCents,
    taxAmountInCents,
    serviceFee,
    cardReaderData,
    createOrderParams,
    isTab,
    itemModels,
    menuSubtype,
    orderNumber,
    orderState,
    paymentCompleted,
    revenueCenterUuid,
    revenueName,
    signature,
    submittedAt,
    uuid,
    invoiceNumber: userAttributes.freedompayInvoiceNumber
  }

  if (orderState === 'closed') {
    yield put(clearRemoteOrderTotal())
  }
  yield put(saveOrder(orderData))
  yield put(syncOrderRequested(uuid))
}

const directTo = (method = 'push', pathOrOptions) => {
  if (pathOrOptions === undefined) return

  if (isString(method) && isString(pathOrOptions)) {
    customHistory[method](pathOrOptions)
  } else if (isString(method) && isPlainObject(pathOrOptions)) {
    customHistory[method](pathOrOptions)
  }
}

export function* updateCardReaderDataSaga(params) {
  const orderId = get(params, 'payload.orderId')
  const order = yield select(getOrder, orderId)
  const newCardReaderData = get(params, 'payload.cardReaderData', {})
  const userAttributes = makeUserAttributes(newCardReaderData)
  const newCreateOrderParams = {
    ...order.createOrderParams,
    userAttributes: userAttributes,
    userName: getUserNameFromCard(newCardReaderData),
    payments: [
      {
        paymentType: get(order, 'createOrderParams.payments[0]', {}).paymentType,
        amountInCents: order.amountInCents,
      }
    ]
  }


  yield put(saveOrder({
    uuid: order.uuid,
    cardReaderData: newCardReaderData,
    createOrderParams: newCreateOrderParams,
    paymentCompleted: true,
    invoiceNumber: userAttributes.freedompayInvoiceNumber
  }))
}

export function* startOfflineCardOrder(params = {}) {
  const isKiosk = get(params, 'payload.isKiosk', false)
  const employeeId = yield verifyCurrentAttendant(isKiosk)
  if (employeeId === undefined) return

  const cardReaderData = get(params, 'payload.cardReaderData', {})
  const signature = params?.payload?.signature
  const tipAmountInCents = params?.payload?.tipAmountInCents ?? 0
  const uuid = cardReaderData.uuid || generateUuid()
  const orderNumber = cardReaderData?.orderNumber ?? params?.payload?.orderNumber
  const submittedAt = Date.now()
  const paymentCompleted = cardReaderData.transType === 'sale'
  const orderState = get(params, 'payload.orderState', 'closed')

  const { menuUuid, revenueCenterUuid, revenueName } = yield menuAndRvcUuids(isKiosk)
  const menu = yield select((state) => getCurrentMenu(state))
  const menuSubtype = get(menu, 'subtype')

  // for kiosk orders, we're passing in line items to avoid race condition of cart being cleared
  // when redirecting to the attract loop
  let lineItems = get(params, 'payload.lineItems')
  if (!lineItems) lineItems = yield select(getCartItems)
  if (isEmpty(lineItems)) {
    yield call(directTo, 'push', '/concession-order')
    toast.error(APPLICATION_STRINGS.ORDER_ONE_ITEM)
    createDataDogLog('error', APPLICATION_STRINGS.ORDER_ONE_ITEM)
    return
  }
  const itemModels = makeLineItemData(lineItems)

  const taxRate = yield select(getTaxRate)
  const taxByItem = yield select(getTaxByItem)
  const getRemoteOrderTotal = makeGetRemoteOrderTotal()
  const remoteOrderTotal = yield select(getRemoteOrderTotal)
  const taxAmountInCents = remoteOrderTotal?.isAvailable ? remoteOrderTotal?.taxAmountInCents ?? 0 : calculateTax(lineItems, taxRate, taxByItem)
  const amountInCents = (remoteOrderTotal?.totalAmountInCents ?? calculateSubtotal(lineItems) + taxAmountInCents) + tipAmountInCents
  const subtotalAmountInCents = remoteOrderTotal?.subtotalInCents ?? amountInCents
  const discountAmountInCents = remoteOrderTotal?.discountAmountInCents ?? 0
  const serviceFee = remoteOrderTotal?.serviceChargeInCents ? { name: remoteOrderTotal?.serviceChargeName, total: remoteOrderTotal?.serviceChargeInCents } : null
  const userAttributes = makeUserAttributes(cardReaderData)
  let promotions = yield select(getSelectedPromotions)
  if (isEmpty(promotions) || !remoteOrderTotal.isAvailable) {
    promotions = undefined
  }

  yield put(setSafUploadComplete(false))

  const createOrderParams = {
    ...makeCreateOrderParams({
      amountInCents,
      tipAmountInCents,
      employeeId,
      isTab: false,
      itemModels,
      menuUuid,
      orderNumber,
      orderState,
      paymentType: 'freedompay_credit',
      submittedAt,
      uuid,
      affiliations: null,
      signature,
      promotions,
    }),
    state: 'approved_offline',
    userAttributes: userAttributes,
    userName: getUserNameFromCard(cardReaderData),
  }

  const orderData = {
    amountInCents,
    tipAmountInCents,
    taxAmountInCents,
    subtotalAmountInCents,
    discountAmountInCents,
    serviceFee,
    cardReaderData,
    createOrderParams,
    itemModels,
    menuSubtype,
    orderNumber,
    paymentCompleted,
    revenueCenterUuid,
    revenueName,
    signature,
    submittedAt,
    uuid,
    invoiceNumber: userAttributes.freedompayInvoiceNumber
  }

  yield put(clearRemoteOrderTotal())
  yield put(saveOfflineOrder(orderData))
  if (remoteOrderTotal?.isAvailable) {
    yield put(syncOrderInitiated(uuid))
    yield put(syncOfflineOrderRequested(uuid))
  }
}

// We just sent a request to FreedomPay to replay all offline approvals fro mthe card reader.
// Here we check the status for particular orders after that is initiated.
export function* getReplayAuthStatus(params = {}) {
  // wait 10 seconds before starting to give replayRequests some time to run first
  // we should re-do this logic when we can get a response from the replayRequests call

  const id = params.orderId
  const order = yield select(getOrder, id)

  const offlineRequestId = get(order, 'cardReaderData.requestId')
  if (!offlineRequestId) {
    yield put(replayAuthRequestFailed({ order, replayStatus: { statusDisplay: 'No request ID for card reader' } }))
    createDataDogLog('error', 'No request ID for card reader')
    return
  }

  const bridgeInstance = PeripheralBridge.getBridge()
  if (!bridgeInstance) {
    yield put(replayAuthRequestFailed({ order, replayStatus: { statusDisplay: 'Could not reach the card reader' } }))
    return
  }

  try {
    const checkStatusPromise = new Promise((resolve, reject) => {
      bridgeInstance.callHandler( 'checkStatus', { 'requestId': offlineRequestId }, (data) => resolve(data))
      setTimeout(() => {
        reject()
      }, MS_GATEWAY_FORCE_CONTINUE)
    })

    const statusData = yield checkStatusPromise

    if (!statusData) {
      yield put(replayAuthRequestFailed({ order, replayStatus: { statusDisplay: 'Request ID does not exists' } }))
      createDataDogLog('error', 'Request ID does not exists')
      return
    }

    const replayStatus = {
      ...statusData,
      statusDisplay: responseStatusString(statusData.responseStatus),
    }

    if (!statusCheckComplete(statusData)) {
      yield put(replayAuthRequestFailed({ order, replayStatus }))
      createDataDogLog('error', 'Replay Status Check Failed', { order, replayStatus })
      return
    }

    let createOrderParams = order.createOrderParams

    // update order state for stadium based on freedompay status
    const isDeclined = approvedOfflineThenDeclined(statusData)
    createOrderParams.state = isDeclined ? 'authorization_failed' : 'completed'

    // Need to do the below checks verbose-ly so that we don't mess up any
    // existing offline functionality with FreedomPay as this is maybe
    // PAX specific
    if (!isEmpty(statusData.authResponseData?.authorizationCode)) {
      createOrderParams.userAttributes.authorizationCode = statusData.authResponseData.authorizationCode
    }

    if (!isEmpty(statusData.receiptData?.approvalCode)) {
      createOrderParams.userAttributes.approvalCode = statusData.receiptData.approvalCode
    }

    if (!isEmpty(statusData.receiptData?.authStatus)) {
      createOrderParams.userAttributes.authStatus = statusData.receiptData.authStatus
    }

    if (!isEmpty(statusData.tokenInformation?.token)) {
      createOrderParams.userAttributes.token = statusData.tokenInformation.token
    }

    // need to update the freedom pay request id for refunds to work
    createOrderParams.userAttributes.freedompayRequestId = statusData.realRequestId
    const cardReaderData = { ...order.cardReaderData, requestId: statusData.realRequestId }

    yield put(saveOrder({ ...order, createOrderParams, cardReaderData, isDeclined }))
    yield put(replayAuthRequestSucceeded({ replayStatus, order }))
    yield put(syncOfflineOrderRequested(id))
  } catch (err) {
    yield put(replayAuthRequestFailed({ order, replayStatus: { statusDisplay: 'Check Status for order timed out' } }))
    createDataDogLog('error', 'Replay Auth Failed', { order, replayStatus: { statusDisplay: 'Check Status for order timed out' } })
    return
  }
}

// Update the unsyncability of an order if it has exceeded the maximum number of retries.
const updateNextSync = (order) => {
  const syncAttempt = order.syncAttempts ?? 0
  if (syncAttempt > 7) {
    order.unsyncable = true
  }
  order.syncAttempts = syncAttempt + 1

  return order
}

export function* syncOrders() {
  const orderIds = yield select(state => getOfflineLocalIds(state))

  if (orderIds.length > 0) {
    createDataDogLog('info', 'Sync Offline Local IDs Started', { offlineLocalIds: orderIds })
    toast.info('A sync to VenueNext is beginning.', { autoClose: 2000 })
  }
  if (orderIds.length <= 0) toast.info('There are no orders to sync to VenueNext.', { autoClose: 2000 })

  for (var i = 0; i < orderIds.length; i++) {
    var safIsUploading = yield select(getSafIsUploading)
    if (!safIsUploading) {
      return
    }

    try {
      yield put(syncOrderRequested(orderIds[i]))
      yield delay(5000)
    } catch (error) {
      // TODO(mkramerl): Turn these into set error actions and display them in the UI.
      toast.error(`Error platform syncing ${orderIds[i]} : ${error}`, { autoClose: 1500 })
      createDataDogLog('error', `Error platform syncing ${orderIds[i]} : ${error}`)
    }
  }
}

export function* syncOrdersReplayAuth() {
  var orderIds = yield select(state => getOfflineCardIds(state))

  if (orderIds.length) {
    createDataDogLog('info', 'Sync Offline Card IDs Started', { offlineCardIds: orderIds })
  }
  for (var i = 0; i < orderIds.length; i++) {
    var safIsUploading = yield select(getSafIsUploading)
    if (!safIsUploading) {
      return
    }

    try {
      yield put(replayAuthRequested(orderIds[i], i === orderIds.length - 1))
      yield delay(5000)
    } catch (error) {
      // TODO(mkramerl): Turn these into set error actions and display them in the UI.
      toast.error(`Error processor syncing ${orderIds[i]} : ${error}`, { autoClose: 1500 })
      createDataDogLog('error', `Error processor syncing ${orderIds[i]} : ${error}`)
    }
  }
}

// Create orders in stadium. Payment is completed (online approval or paid with cash).
export function* syncOrder(params = {}) {
  const order = yield select(state => getOrder(state, params.id))

  if (!order || order.isPreOrder || order.unsyncable) return

  const isKiosk = yield select(state => getIsKiosk(state))
  const employeeId = order.createOrderParams.employeeId || (yield verifyCurrentAttendant(isKiosk))
  if (employeeId === undefined) return

  let syncedOrder = {
    ...order,
  }

  // By the time the flow has gotten here, the assumption is we are online for Kiosk.
  const networkOnline = getNetworkAvailableValue()
  if (!isKiosk && !networkOnline) {
    yield put(syncOrderFailed({ message: "This device was offline while trying to sync." }, syncedOrder))
    return
  }

  // Order was created in stadium with another state (e.g. submitted), which needs to be updated.
  if (order.wasCreatedInStadium && order.orderState !== 'failed') {
    try {
      let createOrderParams = order.createOrderParams
      createOrderParams.state = 'completed'

      const result = yield call(Remote.updatePosOrder, order.uuid, createOrderParams, isKiosk)
      yield put(updateOrderSucceeded(result, order.uuid))
    } catch (err) {
      const applicationMessage = err?.response?.data?.errorMessage;
      const friendlyMessage = err?.response?.data?.friendlyMessage;
      const errorMessage = friendlyMessage ?? applicationMessage;
      const updatedOrder = updateNextSync(order);
      yield put(updateOrderFailed(order.uuid, updatedOrder, err));
      if (errorMessage && err?.message !== "Network Error") {
        toast.error(`${errorMessage}`, { autoClose: false })
        createDataDogLog('error', errorMessage)
      }
    }

    return
  }

  try {
    const result = yield call(Remote.createPosOrder, { ...order.createOrderParams, employeeId }, isKiosk)

    if (order.isMultiPayment) {
      const orderDetails = yield call(Remote.getOrderDetails, order.uuid)
      syncedOrder.payments = yield call(updateOrderPaymentData, syncedOrder, orderDetails)

      yield call(sendMultiPaymentEmails, syncedOrder)
    }
    yield put(syncOrderSucceeded(result, syncedOrder))
  } catch (err) {

    const applicationMessage = err?.response?.data?.errorMessage ?? ''

    if (applicationMessage?.includes("You must login to create orders!")) {
      yield call(directTo, 'push', '/user-login')
      toast.error(APPLICATION_STRINGS.PLEASE_LOGIN)
      createDataDogLog('error', APPLICATION_STRINGS.PLEASE_LOGIN)
    }

    const friendlyMessage = err?.response?.data?.friendlyMessage;
    const errorMessage = friendlyMessage ?? applicationMessage;

    syncedOrder = updateNextSync(syncedOrder)
    yield put(syncOrderFailed(err, syncedOrder));

    if (errorMessage && err?.message !== "Network Error") {
      toast.error(`${errorMessage}`, { autoClose: false })
      createDataDogLog('error', errorMessage)
    }
  }
}

const updateOrderPaymentData = (syncedOrder, orderDetails) => {
  const paymentInfo = orderDetails?.data?.paymentInfo ?? []
  return reduce(syncedOrder?.payments, (payments, payment) => {
    return [
      ...payments,
      {
      ...payment,
      platformUuid: find(paymentInfo, (platformPayment) => platformPayment.shortDescription === payment?.paymentId)?.uuid,
    }]
  }, [])
}

function* sendMultiPaymentEmails(order = {}) {
  const payments = order?.payments ?? []
  for (let i = 0; i < payments.length; i++) {
    if (!isEmpty(payments[i].emails)) {
      yield call(sendOrderEmail, {payload: { orderUuid: order.uuid, emails: payments[i].emails, paymentUuid: payments[i].platformUuid }})
    }
  }
}
// Create order in stadium if payment is approved offline, or if the initial create request failed.
// Update order in stadium if payment was approved offline and is now finished (either failed or approved from card reader)
// For updates, we need to make sure the order is first created in Stadium so we have an offline_approval state transition
export function* syncOfflineOrder(params = {}) {
  const order = yield select(state => getOrder(state, params.id))
  const isKiosk = yield select(state => getIsKiosk(state))

  if (!order) return
  if (order.unsyncable) return

  const networkOnline = getNetworkAvailableValue()
  if (!networkOnline) {
    // TODO (mkramerl): Do something here to indicate that the order can not be updated due to
    // network status.
    return
  }

  // Order was created in stadium with offline_approval state; need to update it
  if (order.wasCreatedInStadium) {
    try {
      const result = yield call(Remote.updatePosOrder, order.uuid, order.createOrderParams, isKiosk)
      yield put(updateOrderSucceeded(result, order.uuid))
    } catch (err) {
      const applicationMessage = err?.response?.data?.errorMessage;
      const friendlyMessage = err?.response?.data?.friendlyMessage;
      const errorMessage = friendlyMessage ?? applicationMessage;
      const updatedOrder = updateNextSync(order);
      yield put(updateOrderFailed(order.uuid, updatedOrder, err));
      if (errorMessage && err?.message !== "Network Error") {
        toast.error(`${errorMessage}`, { autoClose: false })
        createDataDogLog('error', errorMessage)
      }
    }

    return
  }

  // Only verify the employee if this is actually a create action.
  const employeeId = order.createOrderParams.employeeId || (yield verifyCurrentAttendant(isKiosk))
  if (employeeId === undefined) return

  try {
    const result = yield call(Remote.createPosOrder, { ...order.createOrderParams, employeeId, state: 'approved_offline'}, isKiosk)
    yield put(createOfflineOrderSucceeded(result, order.uuid))

    const finished = ['authorization_failed', 'completed'].includes(order.createOrderParams.state)
    if (finished) {
      yield put(syncOrderInitiated(order.uuid))
      yield put(syncOfflineOrderRequested(order.uuid))
    }
  } catch (err) {
    const applicationMessage = err?.response?.data?.errorMessage ?? ''

    if (applicationMessage?.includes("You must login to create orders!")) {
      yield call(directTo, 'push', '/user-login')
      toast.error(APPLICATION_STRINGS.PLEASE_LOGIN)
      createDataDogLog('error', APPLICATION_STRINGS.PLEASE_LOGIN)
    }

    const friendlyMessage = err?.response?.data?.friendlyMessage;
    const errorMessage = friendlyMessage ?? applicationMessage;
    const updatedOrder = updateNextSync(order)
    yield put(createOfflineOrderFailed(order.uuid, updatedOrder, err))
    if (errorMessage && err?.message !== "Network Error") {
      toast.error(`${errorMessage}`, { autoClose: false })
      createDataDogLog('error', errorMessage)
    }
  }
}

export function* _saveRichCheckoutOrder(params = {}) {
  const isKiosk = get(params, 'payload.isKiosk', false)
  const employeeId = yield verifyCurrentAttendant(isKiosk)
  if (employeeId === undefined) return

  const uuid = get(params, 'payload.uuid', generateUuid())
  const orderNumber = get(params, 'payload.orderNumber', nextPosOrderNumber())

  const { menuUuid, revenueCenterUuid, revenueName } = yield menuAndRvcUuids(isKiosk)

  const lineItems = yield select(getCartItems)
  const itemModels = makeLineItemData(lineItems)

  const taxRate = yield select(getTaxRate)
  const taxByItem = yield select(getTaxByItem)

  const remoteOrderTotal = yield select(getRemoteOrderTotal)
  const taxAmountInCents =  remoteOrderTotal?.isAvailable ? remoteOrderTotal?.taxAmountInCents ?? 0 : calculateTax(lineItems, taxRate, taxByItem)
  const amountInCents =  remoteOrderTotal?.totalAmountInCents ?? calculateSubtotal(lineItems) + taxAmountInCents
  const subtotalAmountInCents = remoteOrderTotal?.subtotalInCents ?? amountInCents - taxAmountInCents
  const discountAmountInCents = remoteOrderTotal?.discountAmountInCents ?? 0
  const serviceFee = remoteOrderTotal?.serviceChargeInCents ? { name: remoteOrderTotal?.serviceChargeName, total: remoteOrderTotal?.serviceChargeInCents } : null
  const affiliations = remoteOrderTotal?.isAvailable ? [] : null
  let promotions = yield select(getSelectedPromotions)
  if (isEmpty(promotions) || !remoteOrderTotal.isAvailable) {
    promotions = undefined
  }

  const createOrderParams = {
    ...makeCreateOrderParams({
      amountInCents,
      employeeId,
      itemModels,
      menuUuid,
      orderNumber,
      paymentType: 'mobile_app',
      submittedAt: new Date(),
      uuid,
      affiliations,
      promotions,
    })
  }

  const orderData = {
    amountInCents,
    subtotalAmountInCents,
    discountAmountInCents,
    taxAmountInCents,
    serviceFee,
    createOrderParams,
    itemModels,
    orderNumber,
    paymentCompleted: true,
    revenueCenterUuid,
    revenueName,
    submittedAt: new Date(),
    uuid,
  }

  yield put(saveRichCheckoutOrder(orderData))
  yield put(clearRemoteOrderTotal())
}

export function* addPaymentToOrderInProgressSaga(params = {}) {

  const paymentId = params?.payload?.paymentId ?? ''
  const paymentType = params?.payload?.paymentType
  const tenderAmountInCents = params?.payload?.tenderAmountInCents
  const signature = params?.payload?.signature
  const tipAmountInCents = params?.payload?.tipAmountInCents ?? 0
  const cardReaderData = params?.payload?.cardReaderData
  const tenderDisplayName = params?.payload?.tenderDisplayName
  const remainingTicketBalance = params?.payload?.remainingTicketBalance
  const push = params?.payload?.push

  let userAttributes
  if (cardReaderData) {
    userAttributes = makeUserAttributes(cardReaderData)
  }

  const order = yield select(state => getOrderInProgress(state))
  const balanceDueInCents = order?.balanceDueInCents - tenderAmountInCents
  const orderNumber = cardReaderData?.orderNumber ?? params?.payload?.orderNumber ?? nextPosOrderNumber()

  const orderUpdate = { balanceDueInCents, tipAmountInCents: order?.tipAmountInCents + tipAmountInCents, amountInCents: order?.amountInCents + tipAmountInCents }

  const newPayment = {
    paymentId, paymentType, amountInCents: tenderAmountInCents + tipAmountInCents, orderNumber, tenderDisplayName, tipAmountInCents,
  }

  if (paymentType === 'freedompay_credit') {
    newPayment.userAttributes = userAttributes
    newPayment.signature = signature
    newPayment.cardType = userAttributes.freedompayIssuerName
    newPayment.label = cardReaderData?.cardType
    newPayment.tenderDisplayName = `${userAttributes?.freedompayIssuerName} ${userAttributes?.last_4}`
  }

  if (paymentType === 'loaded_ticket') {
    newPayment.ticketId = params?.payload?.ticketId
    orderUpdate.remainingTicketBalance = remainingTicketBalance
  }

  orderUpdate.payments = [
    ...order.payments,
    newPayment,
  ]

  if (!order.orderNumber) {
    orderUpdate.orderNumber = orderNumber
  }

  if (orderUpdate.payments.length > 1) {
    orderUpdate.isMultiPayment = true
    orderUpdate.displayName = "Multiple Tenders"
  } else {
    orderUpdate.displayName = tenderDisplayName
  }

  yield put(updateOrderInProgress(orderUpdate))
  if (balanceDueInCents === 0) {
    yield call(syncAndSaveOrderInProgress, push)
  } else
  if (paymentId) {
    yield call(directTo, 'push', `/receipt/split-tender/${paymentId}`)
  }
}

export function* syncAndSaveOrderInProgress(push) {
  const isKiosk = yield select(state => getIsKiosk(state))
  const { revenueName } = yield menuAndRvcUuids(isKiosk)
  const order = yield select(state => getOrderInProgress(state))

  const employeeId = yield verifyCurrentAttendant(isKiosk)
  if (employeeId === undefined) return

  const payments = map(order?.payments, (payment) => {
    const tender = {
      paymentType: payment.paymentType,
      amountInCents: payment.amountInCents,
      shortDescription: payment.paymentId,
    }
    if (payment.userAttributes) {
      tender.userAttributes = payment.userAttributes
    }

    if (payment.ticketId) {
      tender.token = payment.ticketId
    }

    return tender
  })

  const submittedAt = new Date()
  let promotions = yield select(getSelectedPromotions)
  if (isEmpty(promotions) || !order.hasRemoteTotal) {
    promotions = undefined
  }
  let menuUuid = yield select((state) => state?.config?.configuration?.menuUuids?.[0] ?? '')

  const createOrderParams = {
    ...makeCreateOrderParams({
      amountInCents: order.amountInCents,
      tipAmountInCents: order.tipAmountInCents,
      employeeId,
      itemModels: order.itemModels,
      menuUuid,
      orderNumber: order.orderNumber,
      submittedAt,
      uuid: order.uuid,
      affiliations: [],
      promotions,
    }),
    payments,
  }

  const orderData = {
    ...order,
    createOrderParams,
    paymentCompleted: true,
    submittedAt,
    revenueName,
  }

  if (order?.payments?.length === 1) {
    orderData.displayName = order?.payments?.[0]?.tenderDisplayName
  }

  yield put(clearRemoteOrderTotal())
  yield put(clearCart())
  yield put(saveOrder(orderData))
  yield put(syncOrderRequested(order?.uuid))

  if (push && isKiosk) {
    yield call(push, `/kiosk/order/summary/${order?.uuid}`)
  } else {
    yield call(directTo, 'push', `/receipt/${order?.uuid}`)
  }
}

export function* watchSyncReplayAuthOrders() {
  yield takeLeading(SYNC_REPLAY_AUTH_ORDERS_REQUESTED, syncOrdersReplayAuth)
}

export function* watchReplayAuthStatus() {
  const queue = yield actionChannel(REPLAY_AUTH_REQUESTED)

  while (true) {
    const payload = yield take(queue)

    yield put(syncOrderInitiated(payload.orderId))
    yield call(getReplayAuthStatus, payload)
  }
}

export function* watchStartOfflineCardOrder() {
  yield takeEvery(START_OFFLINE_CARD_ORDER, startOfflineCardOrder)
}

export function* watchStartOnlineCardOrder() {
  yield takeEvery(START_ONLINE_CARD_ORDER, startOnlineCardOrder)
}

export function* watchSaveRichCheckoutOrder() {
  yield takeEvery(SAVE_RICH_CHECKOUT_ORDER_REQUESTED, _saveRichCheckoutOrder)
}

export function* watchCloseNoOpTender() {
  yield takeEvery(CLOSE_NO_OP_TENDER, closeNoOpTenderSaga)
}

export function* watchSyncOrder() {
  const queue = yield actionChannel(SYNC_ORDER_REQUESTED)

  while (true) {
    const payload = yield take(queue)

    yield put(syncOrderInitiated(payload.id))
    yield call(syncOrder, payload)
  }
}

export function* watchSyncOfflineOrder() {
  const queue = yield actionChannel(SYNC_OFFLINE_ORDER_REQUESTED)

  while (true) {
    const payload = yield take(queue)

    yield put(syncOrderInitiated(payload.id))
    yield call(syncOfflineOrder, payload)
  }
}

export function* watchUpdateCardReaderDataSaga() {
  yield takeEvery(UPDATE_CARD_READER_DATA, updateCardReaderDataSaga)
}

export function* watchSyncOrders() {
  yield takeLeading(SYNC_ORDERS_REQUESTED, syncOrders)
}

export function* watchAddPaymentToOrderInProgress() {
  yield takeLeading(ADD_PAYMENT_TO_ORDER_IN_PROGRESS, addPaymentToOrderInProgressSaga)
}
